import ExcelJS from 'exceljs/dist/exceljs.bare.min.js'
import { saveAs } from 'file-saver'
import { customOrder, RolePGDTest } from 'modules/users/enums/RoleType'
import { validColors } from 'modules/analysis/enum/ColorEnum'
import { FinalStatus } from 'modules/individuals/enums/Status'

const subtractLight = function (color: any, amount: any) {
  let cc = parseInt(color, 16) - amount
  let c: any = cc < 0 ? 0 : cc
  c = c.toString(16)?.length > 1 ? c.toString(16) : `0${c.toString(16)}`
  return c
}

const darken = (color: any, amount: any) => {
  color = color.indexOf('#') >= 0 ? color.substring(1, color?.length) : color
  const amt = parseInt(String((255 * amount) / 100))
  return `#${subtractLight(color?.substring(0, 2), amt)}${subtractLight(
    color.substring(2, 4),
    amt
  )}${subtractLight(color.substring(4, 6), amt)}`
}

const hexToArgb = (hex: any): string => {
  if (!hex || typeof hex !== 'string') return 'FFF0F0F0'
  let c = hex.replace('#', '')
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2]
  if (c.length !== 6) return 'FFF0F0F0'
  return `FF${c.toUpperCase()}`
}

const getAlleleColor = (individualType: any, allele: any): string => {
  if (individualType === 'MutationAdded') {
    return allele?.color || '#ff7b54'
  }
  if (allele?.key && validColors.includes(allele?.color)) {
    return darken(allele.color, 20)
  }
  return allele?.color || '#f0f0f0'
}

const colorP = (
  data: any,
  aName: string,
  fStatus: FinalStatus,
  column: number,
  individualsCount: number,
  pathology?: string,
  childGender?: number,
  data2?: any,
  hasEmbryos?: boolean,
  onlyParents?: boolean
): string => {
  if (aName !== 'P1' && aName !== 'P2') return '#F2EE81'
  if (individualsCount === 2 && !hasEmbryos && onlyParents) return '#F2EE81'
  switch (pathology) {
    case 'X-Linked':
      if (fStatus === FinalStatus.Affected) {
        if (
          data.role === RolePGDTest.MaleMother ||
          data.role === RolePGDTest.FemaleMother ||
          data.role === RolePGDTest.Female ||
          (data.role === RolePGDTest.Child && childGender === 2) ||
          (data.role === RolePGDTest.Embryo && data.embryoGender !== 'male')
        ) {
          return '#ff7b54'
        } else if (column === 1) {
          return '#ff7b54'
        } else return '#96f68a'
      } else if (fStatus === FinalStatus.CarrierFromFather) {
        return column === 1 ? '#96f68a' : '#ff7b54'
      } else if (fStatus === FinalStatus.CarrierFromMother) {
        return column === 2 ? '#96f68a' : '#ff7b54'
      } else return '#96f68a'

    default:
      const child = data2?.preProcessedData?.find((i: any) => i.role === RolePGDTest.Child)
      if (fStatus === FinalStatus.Affected) return '#ff7b54'
      else if (fStatus === FinalStatus.CarrierFromFather) {
        if (
          child &&
          (child.finalStatus === FinalStatus.NonCarrier || child.finalStatus === FinalStatus.Null) &&
          (data.role === RolePGDTest.Male || data.role === RolePGDTest.Female)
        ) {
          return column === 1 ? '#96f68a' : '#ff7b54'
        }
        return column === 1 ? '#96f68a' : '#ff7b54'
      } else if (fStatus === FinalStatus.CarrierFromMother) {
        if (
          child &&
          (child.finalStatus === FinalStatus.NonCarrier || child.finalStatus === FinalStatus.Null) &&
          (data.role === RolePGDTest.Male || data.role === RolePGDTest.Female)
        ) {
          return column === 1 ? '#96f68a' : '#ff7b54'
        }
        return column === 2 ? '#96f68a' : '#ff7b54'
      } else return '#96f68a'
  }
}

function filterUniqueRoles(individual: any[]) {
  const uniqueRoles = new Set()
  return individual?.filter((item: any) => {
    if (!uniqueRoles.has(item.role) || item.role === RolePGDTest.Embryo) {
      uniqueRoles.add(item.role)
      return true
    }
    return false
  })
}

const sortIndividuals = (a: any, b: any): number => {
  if (a.role === b.role) return a.embryo - b.embryo
  return customOrder[a.role] - customOrder[b.role]
}

const isLR = (label: string): string => {
  return (label === 'P1' || label === 'P2')?  label : 'LR' ;
}

export const exportResultsToExcel = async (
  data2: any,
  filteredRows: any[],
  projectName?: string,
  project?: any,
  individualsCount?: number,
  visibleIndividuals?: any[],
  parents?: boolean
): Promise<void> => {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Bioarray PGDSec'
  const sheet = workbook.addWorksheet('PGD Results')

  const source = visibleIndividuals ?? data2?.preProcessedData
  const headerIndividuals =
    filterUniqueRoles(source)?.sort(sortIndividuals) || []

  const hasEmbryos = source?.some((p: any) => p.role === RolePGDTest.Embryo) ?? false

  const headerRow1: string[] = ['Chromosoma', 'Position']
  const headerRow2: string[] = ['', '']
  const headerP1Colors: string[] = ['', '']
  const headerP2Colors: string[] = ['', '']
  const onlyParents: boolean = parents ?? false
  headerIndividuals.forEach((ind: any) => {
    const role = RolePGDTest[ind?.role]
    const roleName = role === 'Embryo' ? ind.name.replace(/_/g, ' ') : role
    headerRow1.push(roleName, '')
    const col1Label = ind.a1ColumnName === 'P1' ? 'P1' : 'LR'
    const col2Label = ind.a2ColumnName === 'P2' ? 'P2' : 'LR'
    headerRow2.push(col1Label, col2Label)
    headerP1Colors.push(
      colorP(ind, isLR(col1Label), ind.finalStatus, 1, individualsCount ?? 0, project?.pathology, project?.childGender, data2, hasEmbryos, onlyParents)
    )
    headerP2Colors.push(
      colorP(ind, isLR(col2Label), ind.finalStatus, 2, individualsCount ?? 0, project?.pathology, project?.childGender, data2, hasEmbryos, onlyParents)
    )
  })

  const headerFont = {
    name: 'Calibri',
    size: 10,
    bold: true,
    color: { argb: 'FFFFFFFF' },
  }
  const headerGrayFill = {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'FF404040' },
  }
  const thinBorder = {
    style: 'thin' as const,
    color: { argb: 'FF808080' },
  }

  const colCount = headerRow1.length

  for (let c = 1; c <= colCount; c++) {
    const cell1 = sheet.getCell(1, c)
    cell1.value = headerRow1[c - 1]
    cell1.font = headerFont
    cell1.fill = headerGrayFill as any
    cell1.alignment = { horizontal: 'center', vertical: 'middle' }
    cell1.border = {
      top: thinBorder,
      left: thinBorder,
      bottom: thinBorder,
      right: thinBorder,
    } as any
  }

  for (let c = 1; c <= colCount; c++) {
    const cell2 = sheet.getCell(2, c)
    cell2.value = headerRow2[c - 1]
    cell2.font = { name: 'Calibri', size: 10, bold: true }
    cell2.alignment = { horizontal: 'center', vertical: 'middle' }
    cell2.border = {
      top: thinBorder,
      left: thinBorder,
      bottom: thinBorder,
      right: thinBorder,
    } as any

    if (c <= 2) {
      cell2.fill = headerGrayFill as any
      cell2.font = headerFont
    } else {
      const indIdx = Math.floor((c - 3) / 2)
      const isP1 = (c - 3) % 2 === 0
      const colorHex = isP1
        ? (headerP1Colors[2 + indIdx] ?? '#f0f0f0')
        : (headerP2Colors[2 + indIdx] ?? '#f0f0f0')
      cell2.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: hexToArgb(colorHex) },
      } as any
    }
  }

  for (let i = 0; i < headerIndividuals.length; i++) {
    const startCol = 3 + i * 2
    sheet.mergeCells(1, startCol, 1, startCol + 1)
  }

  sheet.getColumn(1).width = 10
  sheet.getColumn(2).width = 14
  for (let c = 3; c <= colCount; c++) {
    sheet.getColumn(c).width = 5
  }

  filteredRows.forEach((row: any, rowIdx: number) => {
    const excelRow = rowIdx + 3

    const chromCell = sheet.getCell(excelRow, 1)
    chromCell.value = row.chromosoma
    chromCell.font = { name: 'Calibri', size: 10 }
    chromCell.alignment = { horizontal: 'center', vertical: 'middle' }

    const posCell = sheet.getCell(excelRow, 2)
    posCell.value = row.position
    posCell.font = { name: 'Calibri', size: 10 }
    posCell.alignment = { horizontal: 'center', vertical: 'middle' }

    const rowIndividuals = row.individual || []

    headerIndividuals.forEach((headerInd: any, colIdx: number) => {
      const ind = rowIndividuals.find((i: any) => {
        if (
          headerInd.role === RolePGDTest.Female ||
          headerInd.role === RolePGDTest.Male
        ) {
          return Number(i.role) === Number(headerInd.role)
        }
        if (headerInd.role === RolePGDTest.Embryo) {
          return (
            i.name === headerInd.name ||
            i.sampleName === headerInd.sampleName ||
            (Number(i.role) === RolePGDTest.Embryo && i.embryo === headerInd.embryo)
          )
        }
        return Number(i.role) === Number(headerInd.role)
      })
      if (!ind) return

      const baseCol = 3 + colIdx * 2
      const a1Color = getAlleleColor(row.individualType, ind.a1)
      const a2Color = getAlleleColor(row.individualType, ind.a2)

      const cellA1 = sheet.getCell(excelRow, baseCol)
      cellA1.value = ind.a1?.word || ''
      cellA1.font = { name: 'Calibri', size: 10 }
      cellA1.alignment = { horizontal: 'center', vertical: 'middle' }
      cellA1.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: hexToArgb(a1Color) },
      } as any

      const cellA2 = sheet.getCell(excelRow, baseCol + 1)
      cellA2.value = ind.a2?.word || ''
      cellA2.font = { name: 'Calibri', size: 10 }
      cellA2.alignment = { horizontal: 'center', vertical: 'middle' }
      cellA2.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: hexToArgb(a2Color) },
      } as any
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const filename = projectName ? `${projectName}.xlsx` : 'PGD_Results.xlsx'
  saveAs(blob, filename)
}
